import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Trophy,
} from 'lucide-react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  CircularProgress
} from '@mui/material';
import theme from '../theme';
import ApiService from '../services/api';

const Winners = () => {
  const { t } = useTranslation('winners');
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeYear, setActiveYear] = useState('2025');

  useEffect(() => {
    const fetchWinners = async () => {
      try {
        setLoading(true);
        const data = await ApiService.getWinners();
        setWinners(data.winners || []);
      } catch (error) {
        console.error('Error fetching winners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWinners();
  }, []);

  // Filter winners based on search and filters
  const filteredWinners = useMemo(() => {
    let filtered = winners;
    return filtered;
  }, [winners]);

  return (
    <Box sx={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          pt: { xs: "140px", lg: "100px" },
          pb: { xs: "40px", lg: "50px" },
          minHeight: { xs: '40vh', md: '50vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          overflow: 'visible',
          backgroundImage: `url("/assets/awards_bg.png")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
      >
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle, rgba(212, 165, 32, 0.5)  0%, rgba(0, 0, 0, 0.9) 100%, rgba(0, 0, 0, 0.9) 100%);',
          }}
        />

        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'secondary.light',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: '0.875rem',
              }}
            >
              {t('hero.overline')}
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 'bold',
                mb: 3,
                background: `linear-gradient(135deg, #1A1A1A 30%, ${theme.palette.primary.main} 50%, #1A1A1A 70%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {t('hero.title')}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                mb: 4,
                opacity: 0.9,
                maxWidth: 800,
                color: theme.palette.secondary.main,
                mx: 'auto'
              }}
            >
              {t('hero.subtitle')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                opacity: 0.8,
                maxWidth: 600,
                color: theme.palette.secondary.main,
                mx: 'auto',
                mb: 4
              }}
            >
              {t('hero.description')}
            </Typography>

            {/* Stats bar */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: { xs: 1, md: 0 },
              flexWrap: 'wrap',
              background: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(12px)',
              borderRadius: '14px',
              border: '1px solid rgba(212, 167, 71, 0.25)',
              px: { xs: 2, md: 4 },
              py: { xs: 1.5, md: 2 },
              maxWidth: 700,
              mx: 'auto',
            }}>
              {[
                { value: '30+', label: t('stats.districtsCovered') },
                { value: '12', label: t('stats.categories') },
                { value: '50', label: t('stats.zonalAwards') },
                { value: '200', label: t('stats.districtAwards') },
                { value: '250', label: t('stats.winners') },
              ].map((stat, i, arr) => (
                <React.Fragment key={i}>
                  <Box sx={{ textAlign: 'center', flex: 1, px: { xs: 1, md: 2 } }}>
                    <Typography sx={{ color: '#D4A747', fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.6rem' }, lineHeight: 1 }}>
                      {stat.value}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.55)', fontWeight: 500, fontSize: '0.55rem', mt: 0.5, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                      {stat.label}
                    </Typography>
                  </Box>
                  {i < arr.length - 1 && (
                    <Box sx={{ width: '1px', alignSelf: 'stretch', background: 'rgba(212, 167, 71, 0.2)', display: { xs: 'none', md: 'block' } }} />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Year Tabs */}
      <Box sx={{ py: { xs: 1.5, md: 2 }, backgroundColor: 'white' }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            {['2025', '2024'].map((year) => (
              <Box
                key={year}
                onClick={() => setActiveYear(year)}
                sx={{
                  px: { xs: 2.5, md: 3 },
                  py: { xs: 0.6, md: 0.75 },
                  borderRadius: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  ...(activeYear === year ? {
                    background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                    color: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(212, 167, 71, 0.3)',
                  } : {
                    background: 'transparent',
                    color: '#999',
                    border: '1.5px solid #E0E0E0',
                    '&:hover': { borderColor: '#D4A747', color: '#D4A747' },
                  }),
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', letterSpacing: 0.5 }}>{year}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* 2024 Gallery */}
      {activeYear === '2024' && (
        <Box sx={{ py: { xs: 4, md: 6 }, backgroundColor: '#FAFAFA' }}>
          <Container maxWidth="xl">
            <Box sx={{
              columnCount: { xs: 1, sm: 2, md: 3 },
              columnGap: { xs: '4px', md: '6px' },
            }}>
              {[
                'Anand+BT+%2C+Hassan.JPG',
                'Arun+Kumar%2C+Chikkamagalur.JPG',
                'ChandraShekhar%2C+Mandya.JPG',
                'Dathatri+I+P%2C+Chikkamagalur+Zonal+Award.JPG',
                'Duragappa+Hari%2C+Gadag.JPG',
                'Ganapati%2C+Bidar.JPG',
                'Gopal+Patil%2C+Bidar%2C+Zonal+Award.JPG',
                'Gopalaiah%2C+Raichur.JPG',
                'H+Mahesh%2C+Bellary+Zonal+Award.JPG',
                'Harish+Gowda%2C+Chikkamagalur%2C+Sustainable+Agri+Award.JPG',
                'Imamali+Kanni%2C+Kalburgi.JPG',
                'K+B+Siddegowda%2C+Mandya+Zone.JPG',
                'K+B+Siddegowda%2C+Mandya.JPG',
                'K+Ningegowda+Mysore+Zone.JPG',
                'K+Ningegowda+Mysore.JPG',
                'Kannappa%2C+Shivamogga.JPG',
                'Kishore+KUmar+D+M%2C+Kolar+District.JPG',
                'Mahadevappa%2C+Bellary+Zonal+Award.JPG',
                'Mahesh%2C+B%27luru+Rural+Zone.JPG',
                'Mallappa%2C+Raichur.JPG',
                'Mallika%2C+Shimoga+Zonal+Award.JPG',
                'Mallikarjun%2C+Chikkamagalur.JPG',
                'Mallikarjunagowda+S+Patil%2C+Gadag%2C+Zonal.JPG',
                'Mangala+K+Nilagund%2C+Gadag%2C+District+Award.JPG',
                'Mangala+K+Nilagunda%2C+Gadag.JPG',
                'Manju+Kumar+K+N%2C+Chamarajanagar.JPG',
                'Manjula+H+R%2C+B%27luru+Rural+District.JPG',
                'Manjunath+G+M%2C+Chamarajanagar+2.JPG',
                'Manjunath+G+M%2C+Chamarajanagar.JPG',
                'Manjunath%2C+Dharwad%2C+Zonal+Award.JPG',
                'Mruthyunjaya%2C+Dharwad%2C+Zonal.JPG',
                'Nagapattinam+Kuradagi%2C+Gadag.JPG',
                'Nanagowda%2C+Kalburgi.JPG',
                'Nataraj+H+G%2C+CR+Nagar+District+Award.JPG',
                'Ninganaik%2C+Chitradurga.JPG',
                'Prashanth+Chennappa+Negur%2C+Belagavi.JPG',
                'Rakesha+S%2C+Ramanagar.JPG',
                'Ramanagowda+MH%2C+Davanagere+Zone.JPG',
                'Ramesh+kumar+S%2C+Ramanagar+Zonal+Award.JPG',
                'Ranganath+H%2C+Chitradurga+Zone.JPG',
                'Shivabasayya%2C+Bellary.JPG',
                'Shoukatali+Hazaresab+Lamboonavar%2CDharwad.JPG',
                'Venkatesh+M+N%2C+Chikkaballapur+Zone.JPG',
              ].map((file, i) => {
                const src = `https://bhoomee-awards-submissions.s3.ap-south-1.amazonaws.com/gallery/2024/${file}`;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: Math.min(i * 0.03, 0.5) }}
                    viewport={{ once: true, amount: 0.1 }}
                    style={{ breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', pageBreakInside: 'avoid', marginBottom: 6, display: 'block' }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        transition: 'box-shadow 0.4s ease, transform 0.4s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 40px rgba(212, 167, 71, 0.2)',
                          '& .overlay': { opacity: 1 },
                        },
                      }}
                    >
                      <img
                        src={src}
                        alt="Bhoomee Awards 2024 Winner"
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                        }}
                      />
                      <Box
                        className="overlay"
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '40%',
                          background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)',
                          opacity: 0,
                          transition: 'opacity 0.4s ease',
                          display: 'flex',
                          alignItems: 'flex-end',
                          p: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Trophy size={16} color="#D4A747" />
                          <Typography sx={{ color: '#F4E4BC', fontSize: '0.75rem', fontWeight: 600, letterSpacing: 1 }}>
                            Bhoomee Awards 2024
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                );
              })}
            </Box>
          </Container>
        </Box>
      )}

      {/* 2025 Gallery */}
      {activeYear === '2025' && (
        <Box sx={{ py: { xs: 4, md: 6 }, backgroundColor: '#FAFAFA' }}>
          <Container maxWidth="xl">
            <Box sx={{
              columnCount: { xs: 1, sm: 2, md: 3 },
              columnGap: { xs: '4px', md: '6px' },
            }}>
              {[
                '/images/winner-gallery/winner2.JPG',
                '/images/winner-gallery/winner3.JPG',
                '/images/winner-gallery/winner4.JPG',
                '/images/winner-gallery/winner5.JPG',
                '/images/winner-gallery/winner6.JPG',
                '/images/winner-gallery/winner9.JPG',
                '/images/winner-gallery/winner10.JPG',
                '/images/winner-gallery/winner11.JPG',
                '/images/winner-gallery/winner12.JPG',
                '/images/winner-gallery/winner13.JPG',
                '/images/winner-gallery/winner14.JPG',
                '/images/winner-gallery/winner15.JPG',
                '/images/winner-gallery/winner16.JPG',
                '/images/winner-gallery/winner17.JPG',
                '/images/winner-gallery/winner18.JPG',
                '/images/winner-gallery/winner19.JPG',
              ].map((src, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: Math.min(i * 0.04, 0.5) }}
                  viewport={{ once: true, amount: 0.1 }}
                  style={{ breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', pageBreakInside: 'avoid', marginBottom: 6, display: 'block' }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      transition: 'box-shadow 0.4s ease, transform 0.4s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 40px rgba(212, 167, 71, 0.2)',
                        '& .overlay': { opacity: 1 },
                      },
                    }}
                  >
                    <img
                      src={src}
                      alt="Bhoomee Awards 2025 Winner"
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                      }}
                    />
                    <Box
                      className="overlay"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '40%',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)',
                        opacity: 0,
                        transition: 'opacity 0.4s ease',
                        display: 'flex',
                        alignItems: 'flex-end',
                        p: 2,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Trophy size={16} color="#D4A747" />
                        <Typography sx={{ color: '#F4E4BC', fontSize: '0.75rem', fontWeight: 600, letterSpacing: 1 }}>
                          Bhoomee Awards 2025
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </Container>
        </Box>
      )}

      {/* Hidden 2025 Winners Grid - for future use */}
      {false && <Box sx={{ pt: { xs: 6, md: 1 }, pb: { xs: 6, md: 8 } }}>
        <Container maxWidth="xl">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <CircularProgress sx={{ color: 'primary.main' }} />
            </Box>
          ) : filteredWinners.length === 0 ? (
            <Paper
              sx={{
                p: 6,
                textAlign: 'center',
                borderRadius: '10px',
                backgroundColor: 'white'
              }}
            >
              <Trophy size={48} color="#ADB5BD" style={{ marginBottom: 16 }} />
              <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                {t('filters.noResults.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('filters.noResults.message')}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {filteredWinners.map((winner, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={winner._id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -6 }}
                  >
                    <Card sx={{
                      borderRadius: '10px',
                      overflow: 'hidden',
                      backgroundColor: 'white',
                      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid rgba(212, 167, 71, 0.1)',
                      '&:hover': {
                        boxShadow: '0 15px 45px rgba(212, 167, 71, 0.2)',
                        border: '1px solid rgba(212, 167, 71, 0.3)',
                        '& .winner-photo': {
                          transform: 'scale(1.08)'
                        }
                      }
                    }}>
                      {/* Photo */}
                      <Box
                        sx={{
                          width: '100%',
                          height: 240,
                          position: 'relative',
                          overflow: 'hidden',
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        }}
                      >
                        <Box
                          component="img"
                          src={winner.photoUrl}
                          alt={winner.name}
                          className="winner-photo"
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        />

                        {/* Gradient overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '60%',
                            background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)'
                          }}
                        />

                        {/* Award badge */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
                            color: '#92400E',
                            px: 2,
                            py: 0.5,
                            borderRadius: '50px',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}
                        >
                          {winner.awardType}
                        </Box>

                        {/* Name overlay */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            p: 2
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 800,
                              color: 'white',
                              textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
                              fontSize: '1rem',
                              mb: 0.5,
                              lineHeight: 1.2
                            }}
                          >
                            {winner.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                textTransform: 'capitalize'
                              }}
                            >
                              {winner.awardCategory?.category}
                            </Typography>
                            <Box
                              sx={{
                                width: 3,
                                height: 3,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 255, 255, 0.7)'
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
                                fontWeight: 600,
                                fontSize: '0.7rem'
                              }}
                            >
                              {winner.district}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <CardContent sx={{ p: 2, flex: 1 }}>
                        {/* Award name */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1,
                            mb: 1.5
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '10px',
                              background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <Trophy size={16} color="white" />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: '#1A1A1A',
                                lineHeight: 1.3,
                                fontSize: '0.85rem',
                                mb: 0.5
                              }}
                            >
                              {winner.awardCategory?.awardName}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  color: 'text.secondary'
                                }}
                              >
                                {winner.year}
                              </Typography>
                              {winner.zone && (
                                <>
                                  <Box
                                    sx={{
                                      width: 3,
                                      height: 3,
                                      borderRadius: '50%',
                                      backgroundColor: 'rgba(0, 0, 0, 0.3)'
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.7rem',
                                      color: '#B8901C'
                                    }}
                                  >
                                    {winner.zone}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>}
    </Box>
  );
};

export default Winners;
